import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, ArrowRightLeft, Repeat, Pencil } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAccounts } from '@/composables/financial/useAccounts';
import { accountSchema, type AccountFormData } from '@/lib/validators';
import type { Account } from '@/types/table';
import { financialService } from '@/services/financial.service';
import { accountKeys, transactionKeys } from '@/lib/query-keys';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { DecimalInput } from '@/components/forms/DecimalInput';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { FormDialog } from '@/components/FormDialog';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormCurrencyInput } from '@/components/forms/FormCurrencyInput';
import { formatCurrency } from '@/lib/formatters';

const typeOptions = [
  { label: 'Bank', value: 'BANK' },
  { label: 'Cash', value: 'CASH' },
  { label: 'Credit Card', value: 'CREDIT_CARD' },
  { label: 'Accounts Receivable', value: 'ACCOUNTS_RECEIVABLE' },
  { label: 'Accounts Payable', value: 'ACCOUNTS_PAYABLE' },
];

const currencyOptions = [
  { label: 'USD', value: 'USD' },
  { label: 'EUR', value: 'EUR' },
  { label: 'GBP', value: 'GBP' },
  { label: 'INR', value: 'INR' },
  { label: 'CAD', value: 'CAD' },
  { label: 'AUD', value: 'AUD' },
];

const transferSchema = z.object({
  fromAccountId: z.string().min(1, 'Select source'),
  toAccountId: z.string().min(1, 'Select destination'),
  amount: z.coerce.number().min(0.01, 'Amount must be greater than 0'),
  description: z.string().optional(),
});

// Edit excludes balance (derived from the ledger) and currency
// (would desync transactions tagged with the old currency).
const editAccountSchema = accountSchema.pick({
  name: true,
  type: true,
  accountNumber: true,
  description: true,
});
type EditAccountFormData = z.infer<typeof editAccountSchema>;

export function AccountListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { accounts, isLoading, createAccount, updateAccount } = useAccounts();
  const [showCreate, setShowCreate] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [editTarget, setEditTarget] = useState<Account | null>(null);
  const totalBalance = accounts.reduce((sum, a) => sum + Number(a.balance), 0);

  const form = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: { name: '', type: 'BANK', accountNumber: '', currency: 'USD', description: '', balance: 0 },
  });

  const editForm = useForm<EditAccountFormData>({
    resolver: zodResolver(editAccountSchema),
    defaultValues: { name: '', type: 'BANK', accountNumber: '', description: '' },
  });

  const transferForm = useForm<z.infer<typeof transferSchema>>({
    resolver: zodResolver(transferSchema),
    defaultValues: { fromAccountId: '', toAccountId: '', amount: 0, description: '' },
  });

  const handleCreate = (data: AccountFormData) => {
    createAccount.mutate(data, { onSuccess: () => { setShowCreate(false); form.reset(); } });
  };

  const openEdit = (account: Account) => {
    editForm.reset({
      name: account.name,
      type: account.type as EditAccountFormData['type'],
      accountNumber: account.accountNumber || '',
      description: account.description || '',
    });
    setEditTarget(account);
  };

  const handleEdit = (data: EditAccountFormData) => {
    if (!editTarget) return;
    updateAccount.mutate({ id: editTarget.id, data }, { onSuccess: () => setEditTarget(null) });
  };

  const handleTransfer = async (data: z.infer<typeof transferSchema>) => {
    if (data.fromAccountId === data.toAccountId) {
      toast.error('Source and destination accounts must be different');
      return;
    }
    setTransferring(true);
    try {
      await financialService.transferBetweenAccounts(data);
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      queryClient.invalidateQueries({ queryKey: transactionKeys.all });
      toast.success('Transfer completed');
      setShowTransfer(false);
      transferForm.reset();
    } catch {
      toast.error('Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <PageContainer title="Accounts" description={`Total balance: ${formatCurrency(totalBalance)}`} actions={<>
      <Button variant="outline" onClick={() => setShowTransfer(true)}><Repeat className="mr-2 h-4 w-4" />Transfer</Button>
      <Button onClick={() => setShowCreate(true)}><Plus className="mr-2 h-4 w-4" />Add Account</Button>
    </>}>
      {accounts.length === 0 ? <EmptyState title="No accounts" description="Create your first financial account" actionLabel="Add Account" onAction={() => setShowCreate(true)} /> : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {accounts.map((account) => (
            <Card key={account.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base">{account.name}</CardTitle>
                <div className="flex items-center gap-1">
                  <Badge variant="outline">{account.type.replace('_', ' ')}</Badge>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(account)} aria-label="Edit account">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatCurrency(account.balance, account.currency)}</p>
                {account.accountNumber && <p className="text-xs text-muted-foreground mt-1">Account: {account.accountNumber}</p>}
                {account.description && <p className="text-sm text-muted-foreground mt-2">{account.description}</p>}
                <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => navigate(`/financial/transactions?accountId=${account.id}`)}>
                  <ArrowRightLeft className="mr-2 h-3 w-3" />View Transactions
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <FormDialog open={showCreate} onOpenChange={setShowCreate} title="Add Account" description="Create a new financial account" onSubmit={form.handleSubmit(handleCreate)} isSubmitting={createAccount.isPending}>
        <FormField form={form} name="name" label="Account Name" required />
        <FormSelect form={form} name="type" label="Type" options={typeOptions} required />
        <FormField form={form} name="accountNumber" label="Account Number" />
        <FormSelect form={form} name="currency" label="Currency" options={currencyOptions} />
        <FormCurrencyInput form={form} name="balance" label="Opening Balance" />
        <FormTextarea form={form} name="description" label="Description" />
      </FormDialog>

      <FormDialog
        open={!!editTarget}
        onOpenChange={(o) => { if (!o) setEditTarget(null); }}
        title="Edit Account"
        description="Currency and balance can't be changed here — they're derived from the ledger."
        onSubmit={editForm.handleSubmit(handleEdit)}
        isSubmitting={updateAccount.isPending}
        submitLabel="Save"
      >
        <FormField form={editForm} name="name" label="Account Name" required />
        <FormSelect form={editForm} name="type" label="Type" options={typeOptions} required />
        <FormField form={editForm} name="accountNumber" label="Account Number" />
        <FormTextarea form={editForm} name="description" label="Description" />
      </FormDialog>

      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer</DialogTitle>
          </DialogHeader>
          <form onSubmit={transferForm.handleSubmit(handleTransfer)} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>From</Label>
              <Select value={transferForm.watch('fromAccountId')} onValueChange={(v) => { transferForm.setValue('fromAccountId', v); if (v === transferForm.watch('toAccountId')) transferForm.setValue('toAccountId', ''); }}>
                <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="external">External</SelectItem>
                  {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({formatCurrency(a.balance, a.currency)})</SelectItem>)}
                </SelectContent>
              </Select>
              {transferForm.formState.errors.fromAccountId && <p className="text-xs text-destructive">{transferForm.formState.errors.fromAccountId.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>To</Label>
              <Select value={transferForm.watch('toAccountId')} onValueChange={(v) => transferForm.setValue('toAccountId', v)}>
                <SelectTrigger><SelectValue placeholder="Select destination" /></SelectTrigger>
                <SelectContent>
                  {transferForm.watch('fromAccountId') !== 'external' && <SelectItem value="external">External</SelectItem>}
                  {accounts.filter(a => a.id !== transferForm.watch('fromAccountId') || transferForm.watch('fromAccountId') === 'external').map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({formatCurrency(a.balance, a.currency)})</SelectItem>)}
                </SelectContent>
              </Select>
              {transferForm.formState.errors.toAccountId && <p className="text-xs text-destructive">{transferForm.formState.errors.toAccountId.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>Amount</Label>
              <DecimalInput placeholder="0.00" {...transferForm.register('amount')} />
              {transferForm.formState.errors.amount && <p className="text-xs text-destructive">{transferForm.formState.errors.amount.message}</p>}
            </div>
            {(transferForm.watch('fromAccountId') === 'external' || transferForm.watch('toAccountId') === 'external') && (
              <div className="space-y-2">
                <Label>Description</Label>
                <Input placeholder="e.g. Client payment, Vendor payout..." {...transferForm.register('description')} />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowTransfer(false)}>Cancel</Button>
              <Button type="submit" disabled={transferring}>{transferring ? 'Transferring...' : 'Transfer'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
